import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useCardSuggestions } from '../hooks/useCardSuggestions';
import { CardType, SchemaField, CareerCardSchema } from '../types/careerCard';
import { IoSparklesOutline, IoChevronDownOutline, IoChevronForwardOutline } from 'react-icons/io5';
import { useCareerCard } from '../hooks/useCareerCard';

interface CardSuggestionsProps {
  onSelect: (type: CardType, initialData: Record<string, unknown>) => void;
  existingSuggestions?: any[];
  hasGeneratedSuggestions?: boolean;
  onSuggestionsGenerated?: (suggestions: any[]) => void;
}

interface CardSchema extends CareerCardSchema {
  card_type: string;
  schema: Record<string, SchemaField>;
  is_active: boolean;
}

interface SuggestionPreviewProps {
  suggestion: any;
  schemas: CardSchema[];
  onSelect: (type: CardType, initialData: Record<string, unknown>) => void;
}

const parseFieldValue = (value: any, fieldSchema: SchemaField | undefined): any => {
  if (!fieldSchema) return value;

  if (fieldSchema.type === 'nested' && typeof value === 'string') {
    try {
      // Attempt to parse stringified JSON
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.map(item => {
          if (typeof item === 'string') {
            try {
              return JSON.parse(item);
            } catch {
              return item;
            }
          }
          return item;
        });
      }
      return parsed;
    } catch (e) {
      console.warn('Failed to parse nested field value:', value);
      return value;
    }
  }

  return value;
};

const SuggestionPreview: React.FC<SuggestionPreviewProps> = ({ suggestion, schemas, onSelect }) => {
  const schema = schemas.find(s => s.card_type === suggestion.type);
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Parse all fields according to schema
  const parsedData = React.useMemo(() => {
    if (!schema) return suggestion.suggested_fields;

    return Object.entries(suggestion.suggested_fields).reduce((acc, [key, value]) => {
      const fieldSchema = schema.schema[key];
      acc[key] = parseFieldValue(value, fieldSchema);
      return acc;
    }, {} as Record<string, any>);
  }, [suggestion, schema]);

  const formatFieldName = (name: string) => {
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const toggleExpand = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsExpanded(prev => !prev);
  };

  const renderNestedField = (fieldName: string, content: any): React.ReactNode => {
    if (!content) return null;

    const fieldSchema = schema?.schema[fieldName];
    
    if (Array.isArray(content)) {
      return (
        <div className="space-y-2">
          {content.map((item, index) => (
            <div key={index} className="pl-4 border-l-2 border-gray-200 dark:border-gray-700">
              {typeof item === 'object' && item !== null ? (
                Object.entries(item).map(([key, value]) => (
                  <div key={key} className="text-sm">
                    <span className="font-medium">{formatFieldName(key)}:</span>{' '}
                    <span className="text-gray-600 dark:text-gray-400">
                      {String(value)}
                    </span>
                  </div>
                ))
              ) : (
                <span className="text-sm text-gray-600 dark:text-gray-400">{String(item)}</span>
              )}
            </div>
          ))}
        </div>
      );
    }

    if (typeof content === 'object' && content !== null) {
      return (
        <div className="space-y-1">
          {Object.entries(content).map(([key, value]) => (
            <div key={key} className="text-sm">
              <span className="font-medium">{formatFieldName(key)}:</span>{' '}
              {renderNestedField(key, value)}
            </div>
          ))}
        </div>
      );
    }

    return <span className="text-sm text-gray-600 dark:text-gray-400">{String(content)}</span>;
  };

  // Count the number of fields for the collapsed summary
  const fieldCount = Object.keys(parsedData).filter(
    key => !['title', 'type', 'overview', 'custom_tags', 'date_tags'].includes(key)
  ).length;

  return (
    <div 
      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
      onClick={() => onSelect(suggestion.type as CardType, {
        title: suggestion.title,
        overview: suggestion.overview,
        ...parsedData
      })}
    >
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium">{suggestion.title}</h4>
        <span className="text-sm px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded">
          {suggestion.type}
        </span>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
        {suggestion.overview}
      </p>
      
      <div className="border-t border-gray-200 dark:border-gray-700 pt-2">
        <div 
          className="flex items-center gap-1 cursor-pointer group mb-2"
          onClick={toggleExpand}
        >
          {isExpanded ? (
            <IoChevronDownOutline className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300" />
          ) : (
            <IoChevronForwardOutline className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300" />
          )}
          <div className="font-medium text-sm text-gray-700 dark:text-gray-300">
            Details {!isExpanded && `(${fieldCount} field${fieldCount !== 1 ? 's' : ''})`}
          </div>
        </div>
        
        {isExpanded && (
          <div className="space-y-3 mt-2">
            {Object.entries(parsedData)
              .filter(([key]) => !['title', 'type', 'overview', 'custom_tags', 'date_tags'].includes(key))
              .map(([key, value]) => (
                <div key={key}>
                  <div className="font-medium text-sm text-gray-700 dark:text-gray-300 mb-1">
                    {formatFieldName(key)}:
                  </div>
                  <div className="ml-4">
                    {renderNestedField(key, value)}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const CardSuggestions: React.FC<CardSuggestionsProps> = ({ 
  onSelect,
  existingSuggestions,
  hasGeneratedSuggestions = false,
  onSuggestionsGenerated
}) => {
  const { suggestions: hookSuggestions, isLoading, error, fetchSuggestions } = useCardSuggestions();
  const [hasTriggered, setHasTriggered] = React.useState(hasGeneratedSuggestions);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const { schemas } = useCareerCard();
  const activeSchemas = schemas.filter(schema => schema.is_active);

  useEffect(() => {
    if (hoveredIndex !== null && buttonRefs.current[hoveredIndex]) {
      const button = buttonRefs.current[hoveredIndex];
      if (button) {
        const rect = button.getBoundingClientRect();
        const scrollY = window.scrollY;
        const scrollX = window.scrollX;

        // Calculate position to show tooltip to the right of the button
        setTooltipPosition({
          top: rect.top + scrollY,
          left: rect.right + scrollX + 8 // 8px offset from the button
        });
      }
    }
  }, [hoveredIndex]);

  React.useEffect(() => {
    if (hasTriggered && hookSuggestions.length > 0 && onSuggestionsGenerated) {
      onSuggestionsGenerated(hookSuggestions);
    }
  }, [hookSuggestions, onSuggestionsGenerated, hasTriggered]);

  const handleGetSuggestions = () => {
    setHasTriggered(true);
    fetchSuggestions();
  };

  const suggestions = existingSuggestions || hookSuggestions;

  if (!hasTriggered) {
    return (
      <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
        <IoSparklesOutline className="w-8 h-8 text-blue-500 mb-3" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Get AI Suggestions
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-300 text-center mb-4">
          Let AI analyze your recent notes and suggest relevant career cards to create
        </p>
        <button
          onClick={handleGetSuggestions}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors duration-200"
        >
          Get Suggestions
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-6 border-2 border-red-200 dark:border-red-800 rounded-lg">
        <p className="text-sm text-red-500 dark:text-red-400 mb-3">
          Failed to load suggestions
        </p>
        <button
          onClick={handleGetSuggestions}
          className="px-4 py-2 text-sm text-blue-500 hover:text-blue-600"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
        <div className="w-8 h-8 border-t-2 border-b-2 border-blue-500 rounded-full animate-spin mb-3" />
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Analyzing your notes...
        </p>
      </div>
    );
  }

  if (suggestions.length === 0 && hasTriggered) {
    return (
      <div className="text-center p-6 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
          No suggestions found based on your recent notes
        </p>
        <button
          onClick={handleGetSuggestions}
          className="px-4 py-2 text-sm text-blue-500 hover:text-blue-600"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Suggested Cards
        </h3>
        <button
          onClick={handleGetSuggestions}
          className="text-sm text-blue-500 hover:text-blue-600"
        >
          Refresh
        </button>
      </div>
      <div className="space-y-4">
        {suggestions.map((suggestion, index) => (
          <SuggestionPreview
            key={index}
            suggestion={suggestion}
            schemas={activeSchemas}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}; 